import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";

import LoginPage from "@/pages/LoginPage";
import ActivatePage from "@/pages/ActivatePage";
import CheckoutPage from "@/pages/CheckoutPage";
import ReactivatePage from "@/pages/ReactivatePage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

import MemberLayout from "@/components/layouts/MemberLayout";
import DashboardPage from "@/pages/DashboardPage";
import CoursesPage from "@/pages/CoursesPage";
import CourseDetailPage from "@/pages/CourseDetailPage";
import RecipesPage from "@/pages/RecipesPage";
import CaloriesCalculatorPage from "@/pages/CaloriesCalculatorPage";
import LegalPage from "@/pages/LegalPage";
import RecipeDetailPage from "@/pages/RecipeDetailPage";
import ImportedRecipeDetailPage from "@/pages/ImportedRecipeDetailPage";
import CommunityPage from "@/pages/CommunityPage";
import AIChatPage from "@/pages/AIChatPage";
import SubscriptionExpiredPage from "@/pages/SubscriptionExpiredPage";
import SettingsPage from "@/pages/SettingsPage";

import AdminLayout from "@/components/layouts/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminMembers from "@/pages/admin/AdminMembers";
import AdminActivations from "@/pages/admin/AdminActivations";
import AdminPurchases from "@/pages/admin/AdminPurchases";
import AdminCourses from "@/pages/admin/AdminCourses";
import AdminRecipes from "@/pages/admin/AdminRecipes";
import AdminCommunity from "@/pages/admin/AdminCommunity";
import AdminChat from "@/pages/admin/AdminChat";
import AdminWebhooks from "@/pages/admin/AdminWebhooks";
import AdminEmailTemplates from "@/pages/admin/AdminEmailTemplates";
import AdminEmailSequences from "@/pages/admin/AdminEmailSequences";
import AdminEmailQueue from "@/pages/admin/AdminEmailQueue";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminAttribution from "@/pages/admin/AdminAttribution";
import AdminLegacyImports from "@/pages/admin/AdminLegacyImports";
import AdminBuyers from "@/pages/admin/AdminBuyers";
import AdminInbox from "@/pages/admin/AdminInbox";
import AdminJourney from "@/pages/admin/AdminJourney";
import AdminSystem from "@/pages/admin/AdminSystem";
import AdminBroadcasts from "@/pages/admin/AdminBroadcasts";
import AdminWhatsApp from "@/pages/admin/AdminWhatsApp";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminAutomatedFlows from "@/pages/admin/AdminAutomatedFlows";
import AdminDiagnostics from "@/pages/admin/AdminDiagnostics";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminSiteMap from "@/pages/admin/AdminSiteMap";
import AdminUsage from "@/pages/admin/AdminUsage";
import AdminUserUsage from "@/pages/admin/AdminUserUsage";
import AdminAIKnowledge from "@/pages/admin/AdminAIKnowledge";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminMedia from "@/pages/admin/AdminMedia";
import UnsubscribePage from "@/pages/UnsubscribePage";
import FlexLandingPage from "@/pages/FlexLandingPage";
import FlexQuizPage from "@/pages/FlexQuizPage";
import { SHOW_QUIZ } from "@/config/featureFlags";

function BrandSplash() {
  return (
    <div className="min-h-screen grid place-items-center bg-background" data-testid="brand-splash">
      <div className="flex flex-col items-center gap-4 animate-fadein-up">
        <img
          src="/faisalfit-logo.png"
          alt=""
          className="h-28 w-28 rounded-full shadow-lg animate-badge-pulse"
        />
        <div className="h-1 w-24 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 opacity-70 overflow-hidden">
          <div className="h-full w-full bg-white/40 shimmer-overlay" />
        </div>
      </div>
    </div>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <BrandSplash />;
  // Anonymous visitors land on the marketing funnel
  if (!user) return <Navigate to="/flex" replace />;
  const roles = user.roles || [];
  if (roles.some((r) => ["admin", "super_admin"].includes(r))) return <Navigate to="/admin" replace />;
  if (roles.includes("moderator")) return <Navigate to="/admin/community" replace />;
  return <Navigate to="/dashboard" replace />;
}

function PrivateRoute() {
  const { user, loading } = useAuth();
  if (loading) return <BrandSplash />;
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

/**
 * Wraps the member area. Redirects a formerly-paid member whose subscription
 * has fully expired, OR an admin-granted trial that just ran out, to the
 * "subscription expired" page so they can re-pay.
 * Admins/moderators are exempt (the backend already excludes them from both flags).
 * The /settings page stays reachable so they can still manage their profile.
 */
function SubscriptionGuard() {
  const { user } = useAuth();
  if (user?.subscription_expired || user?.trial_expired) {
    return <Navigate to="/subscription-expired" replace />;
  }
  return <Outlet />;
}

function AdminRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">جاري التحميل...</div>;
  if (!user) return <Navigate to="/login" replace />;
  const isPrivileged = user.roles?.some((r) => ["admin", "super_admin", "moderator"].includes(r));
  if (!isPrivileged) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-center" dir="rtl" />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/flex" element={<FlexLandingPage />} />
          <Route
            path="/flex/quiz"
            element={SHOW_QUIZ ? <FlexQuizPage /> : <Navigate to="/checkout?funnel=flex&utm_source=flex" replace />}
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/activate" element={<ActivatePage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/reactivate" element={<ReactivatePage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/privacy" element={<LegalPage />} />
          <Route path="/terms" element={<LegalPage />} />
          <Route path="/refund" element={<LegalPage />} />
          <Route path="/health-disclaimer" element={<LegalPage />} />

          <Route element={<PrivateRoute />}>
            <Route path="/subscription-expired" element={<SubscriptionExpiredPage />} />
            <Route element={<MemberLayout />}>
              <Route path="/settings" element={<SettingsPage />} />
            </Route>
            <Route element={<SubscriptionGuard />}>
              <Route element={<MemberLayout />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/courses" element={<CoursesPage />} />
                <Route path="/courses/:courseId" element={<CourseDetailPage />} />
                <Route path="/recipes" element={<RecipesPage />} />
                <Route path="/calories" element={<CaloriesCalculatorPage />} />
                <Route path="/recipes/:recipeId" element={<RecipeDetailPage />} />
                <Route path="/imported/:recipeId" element={<ImportedRecipeDetailPage />} />
                <Route path="/community" element={<CommunityPage />} />
                <Route path="/chat" element={<Navigate to="/community" replace />} />
                <Route path="/ai-chat" element={<AIChatPage />} />
              </Route>
            </Route>
          </Route>

          <Route element={<AdminRoute />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/members" element={<AdminMembers />} />
              <Route path="/admin/invites" element={<AdminActivations />} />
              <Route path="/admin/purchases" element={<AdminPurchases />} />
              <Route path="/admin/courses" element={<AdminCourses />} />
              <Route path="/admin/recipes" element={<AdminRecipes />} />
              <Route path="/admin/community" element={<AdminCommunity />} />
              <Route path="/admin/chat" element={<AdminChat />} />
              <Route path="/admin/webhooks" element={<AdminWebhooks />} />
              <Route path="/admin/emails/templates" element={<AdminEmailTemplates />} />
              <Route path="/admin/emails/sequences" element={<AdminEmailSequences />} />
              <Route path="/admin/emails/queue" element={<AdminEmailQueue />} />
              <Route path="/admin/leads" element={<AdminLeads />} />
              <Route path="/admin/attribution" element={<AdminAttribution />} />
              <Route path="/admin/legacy-imports" element={<AdminLegacyImports />} />
              <Route path="/admin/buyers" element={<AdminBuyers />} />
              <Route path="/admin/inbox" element={<AdminInbox />} />
              <Route path="/admin/journey" element={<AdminJourney />} />
              <Route path="/admin/system" element={<AdminSystem />} />
              <Route path="/admin/broadcasts" element={<AdminBroadcasts />} />
              <Route path="/admin/whatsapp" element={<AdminWhatsApp />} />
              <Route path="/admin/plans" element={<AdminPlans />} />
              <Route path="/admin/coupons" element={<AdminCoupons />} />
              <Route path="/admin/automated-flows" element={<AdminAutomatedFlows />} />
              <Route path="/admin/diagnostics" element={<AdminDiagnostics />} />
              <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
              <Route path="/admin/sitemap" element={<AdminSiteMap />} />
              <Route path="/admin/usage" element={<AdminUsage />} />
              <Route path="/admin/user-usage" element={<AdminUserUsage />} />
              <Route path="/admin/ai-knowledge" element={<AdminAIKnowledge />} />
              <Route path="/admin/notifications" element={<AdminNotifications />} />
              <Route path="/admin/media" element={<AdminMedia />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
